/**
 * Supabase Client Configuration
 * 
 * This client handles all authentication with Supabase.
 * User data/profiles remain in MongoDB - only auth is through Supabase.
 */
import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error('[Supabase] Missing configuration. Check REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: localStorage,
    storageKey: 'supabase_auth_token',
  },
  global: {
    fetch: (url, options) => {
      // Add timeout to all Supabase requests
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      return fetch(url, {
        ...options,
        signal: controller.signal
      }).finally(() => clearTimeout(timeoutId));
    }
  }
});

/**
 * Get the current session's access token for API calls
 */
export const getAccessToken = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) {
    console.error('[Supabase] Error getting session:', error);
    return null;
  }
  if (session?.access_token) {
    console.log('[Supabase] Got access token for:', session.user?.email);
  } else {
    console.log('[Supabase] No session available');
  }
  return session?.access_token || null;
};

/**
 * Check if user is currently authenticated
 */
export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

export default supabase;
